import { get, post } from './client'

/**
 * Placing an order, and reading the ones already placed.
 *
 * This is the only write a customer ever makes, and it is the one that must never happen
 * twice.
 */

/**
 * `POST /api/orders`
 *
 * **`Idempotency-Key` is required by the server**, not optional. A double-tapped submit,
 * a retry after a dropped connection, or a browser replaying the request on a flaky
 * signal all arrive as the same key, and the server answers with the original order
 * instead of creating a second one. A second order means a second delivery fee and a
 * second commission out of a rider's wallet.
 *
 * The key must therefore be **stable across retries of the same intent** — see the ref in
 * WizardContext, which mints one per form and reuses it. Generating a fresh key per click
 * would defeat the entire mechanism while looking like it worked.
 *
 * Dispatch happens **inside this request**: a rider is chosen and pushed before it
 * returns, which is why it takes a moment and why the response already carries a
 * `dispatchState`.
 */
export const createOrder = (body, idempotencyKey, quoteToken = null) =>
  post('/api/orders', body, { idempotencyKey, quoteToken })

/**
 * `POST /api/orders/quote`
 *
 * What delivery will cost, before there is an order to charge it to. Answers 200 either
 * way: `available: false` with the matcher's reason counts is a shop that is busy, not a
 * failure, and the summary renders it as a sentence.
 *
 * Picking a rider **holds them for thirty seconds**, so this is not free to call in a
 * loop — every extra call takes a rider out of somebody else's pool for half a minute.
 */
export const quoteDelivery = (route, { signal } = {}) =>
  post('/api/orders/quote', route, { signal })

/**
 * `GET /api/orders`
 *
 * The customer's own orders, newest first. `status` is the only filter the server takes.
 */
export async function getOrders({ status = null, page = 1, limit } = {}) {
  const params = new URLSearchParams()

  if (status && status !== 'all') params.set('status', status)
  if (page) params.set('page', String(page))
  if (limit) params.set('limit', String(limit))

  return get(`/api/orders?${params}`)
}

/**
 * `GET /api/orders/:orderId`
 *
 * Two callers, two ways of being allowed: the customer's own session, or a `shareToken`
 * from a link they passed to whoever is waiting for the delivery.
 */
export const getOrder = (orderId, { shareToken = null } = {}) => (
  shareToken
    ? getSharedOrder(orderId, shareToken)
    : get(`/api/orders/${encodeURIComponent(orderId)}`)
)

/**
 * The shared view, and it deliberately does **not** go through `./client`.
 *
 * Every other call in this app inherits three things from that module: a bearer token, a
 * refresh-on-401 retry, and a session-expired handler that clears storage and can bounce
 * the app to sign-in. All three are wrong here, and the third is the one that would be
 * reported as "the link is broken":
 *
 *   - The person opening this has **no account**. They are in Messenger's in-app browser
 *     on a phone that has never signed in to anything of ours.
 *   - Worse, they might have one. A customer opening their *own* shared link would send
 *     a stale bearer, get a 401 the token path never asked for, and trigger a refresh —
 *     or a sign-out — on a page that needed no session at all.
 *
 * So: a bare fetch, with no `Authorization` header ever attached. The absence of auth
 * code on this path is the guarantee, not an oversight. `trackService.js` said the same
 * thing about the page this one replaces.
 */
async function getSharedOrder(orderId, shareToken) {
  const path = `/api/orders/${encodeURIComponent(orderId)}?t=${encodeURIComponent(shareToken)}`
  const response = await fetch(`${import.meta.env.VITE_API_URL}${path}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })

  let body = null

  try {
    body = await response.json()
  } catch {
    // A gateway or a proxy answering with HTML. Falls through to the status check, which
    // reports the status honestly rather than a JSON parse error three layers away.
  }

  if (!response.ok || !body?.success) {
    /**
     * The status is what the screen branches on, and **410 is not 404**. One means the
     * link never named anything; the other means the delivery finished and the link has
     * done its job. Collapsing them makes a completed delivery look like a broken app.
     */
    throw new SharedOrderError(body?.error ?? 'We could not open this link.', response.status)
  }

  return body
}

export class SharedOrderError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'SharedOrderError'
    this.status = status
  }
}

export class EstimateError extends Error {
  constructor(message, status) {
    super(message)
    this.name = 'EstimateError'
    this.status = status
  }
}

/**
 * `POST /api/orders/estimate`
 *
 * What delivery would cost, for a shop that has been asked *"magkano po padala?"* in a
 * Messenger thread and needs the answer in the next few seconds.
 *
 * **Not `quoteDelivery`, and the difference is a rider.** That one holds a real rider for
 * thirty seconds so the figure on the summary is the figure charged. Doing that here would
 * spend the town's rider supply on questions that never become orders — see
 * `useDeliveryQuote` and docs/intent/customer-app-friction.md. This one prices the two pins
 * and touches nothing.
 *
 * A bare fetch, for the same reason `getSharedOrder` is one directly above: the shop
 * opening the price checker has no account, and a stale bearer from a customer who does
 * would trigger a refresh — or a sign-out — on a page that needs no session at all.
 *
 * Answers a **range**, not a figure: `deliveryFeeFrom` is a rider already at the shop,
 * `deliveryFeeTo` is the rider-distance fee at its cap. The shop reads out the top half
 * and is never made to under-quote their own customer.
 */
export async function estimateDelivery({ partnerSlug, pickup, dropoff }) {
  const response = await fetch(`${import.meta.env.VITE_API_URL}/api/orders/estimate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      partnerSlug,
      pickupLatitude: pickup.lat,
      pickupLongitude: pickup.lng,
      dropoffLatitude: dropoff.lat,
      dropoffLongitude: dropoff.lng,
    }),
  })

  let body = null

  try {
    body = await response.json()
  } catch {
    // A gateway answering with HTML. The status check below reports that honestly.
  }

  if (!response.ok || !body?.success) {
    /**
     * The server's own sentence, not a generic one. A 422 here is *"Machi Express delivers
     * within Los Baños and Bay only. That drop-off is not inside either town"* — which
     * names the one thing the shop can act on, and is the most common refusal this page
     * will ever show.
     */
    throw new EstimateError(body?.error ?? 'Hindi makuha ang presyo ngayon.', response.status)
  }

  return body.data
}

/**
 * `POST /api/orders/:orderId/cancel`
 *
 * Only while nothing is on the road. Once a rider has accepted, the server refuses with a
 * 409 and the customer has to talk to the partner — the check runs inside a transaction,
 * so a rider accepting at the same moment cannot be overwritten by a cancel that started
 * a fraction earlier.
 */
export const cancelOrder = (orderId) =>
  post(`/api/orders/${encodeURIComponent(orderId)}/cancel`, {})
